// src/features/merchantConfig/components/UnsavedChangesPrompt.jsx
//
// Two protections against losing unsaved changes:
//   1. Browser-level: window `beforeunload` listener that fires the native
//      "Changes you made may not be saved" dialog if the merchant tries to
//      close the tab or hit refresh.
//   2. App-level: a small modal the parent surfaces when an intra-app
//      navigation is requested while edit mode is dirty.
//
// The browser-level guard is unconditional (always installs while dirty);
// the modal is rendered declaratively by the parent (e.g. when the user
// clicks another sidebar category).

import React, { useEffect } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Installs a `beforeunload` listener for as long as `enabled` is true.
 *
 * @param {boolean} enabled
 */
export function useBeforeUnloadGuard(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      // Setting `returnValue` is the canonical way to trigger the browser's
      // native "leave / stay" dialog. The exact text shown is browser-controlled.
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [enabled]);
}

/**
 * @param {{
 *   open: boolean,
 *   dirtyCount: number,
 *   onDiscard: () => void,
 *   onCancel: () => void,
 *   onSave?: () => void,
 *   canSave?: boolean,
 * }} props
 */
export default function UnsavedChangesPrompt({
  open,
  dirtyCount,
  onDiscard,
  onCancel,
  onSave,
  canSave,
}) {
  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc closes (treated as Cancel).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-prompt-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="flex-1 space-y-1">
            <h2
              id="unsaved-prompt-title"
              className="text-base font-semibold text-gray-900"
            >
              You have unsaved changes
            </h2>
            <p className="text-sm text-gray-600">
              {dirtyCount === 1
                ? "1 field"
                : `${dirtyCount} fields`}{" "}
              will be lost if you leave without saving.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            Stay on this page
          </button>
          <button
            type="button"
            onClick={onDiscard}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
          >
            Discard changes
          </button>
          {onSave && (
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              Save changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
